import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Logger } from '@aiwyn/shared';
import { filter, map, startWith, tap } from 'rxjs/operators';
import {
  CXP_ROUTE_PARAM_DELIVERABLE_ID,
  CXP_ROUTE_PARAM_ITEM_ID,
  CXP_ROUTE_PARAM_REQUEST_ID,
} from '../models/routing-constants';
import { SetCxpActiveRouteParams } from '../state/cxp-tasks.actions';

interface CxpRouteParams {
  requestId: number | null;
  deliverableId: number | null;
  itemId: number | null;
}

const EMPTY_PARAMS: CxpRouteParams = { requestId: null, deliverableId: null, itemId: null };

@Injectable({ providedIn: 'root' })
export class CxpRouteParamsService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly logger = inject(Logger);

  readonly paramIds = signal<CxpRouteParams>(EMPTY_PARAMS);

  readonly requestId = computed(() => this.paramIds().requestId);
  readonly deliverableId = computed(() => this.paramIds().deliverableId);
  /** Item shown in the redesigned CRL side panel (named `panel` outlet child). */
  readonly itemId = computed(() => this.paramIds().itemId);

  constructor() {
    this.setupParams();
  }

  private setupParams(): void {
    // Seed from the current snapshot so direct page loads don't start with null.
    const initialValue: CxpRouteParams = this.readParamsFromCurrentSnapshot();

    const routeIds$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.readParamsFromCurrentSnapshot()),
    );

    routeIds$
      .pipe(
        startWith(initialValue),
        takeUntilDestroyed(),
        tap((params) => {
          this.paramIds.set(params);
          this.store.dispatch(new SetCxpActiveRouteParams(params.requestId, params.deliverableId, params.itemId));
        }),
      )
      .subscribe({
        error: (err) => this.logger.error('[CxpRouteParamsService] route param tracking failed:', err),
      });
  }

  private readParamsFromCurrentSnapshot(): CxpRouteParams {
    return {
      requestId: this.getParamFromRouteTree(CXP_ROUTE_PARAM_REQUEST_ID),
      deliverableId: this.getParamFromRouteTree(CXP_ROUTE_PARAM_DELIVERABLE_ID),
      // `itemId` lives on the named `panel` outlet child, which is NOT on the
      // primary `firstChild` chain — the full-tree walk below descends every
      // outlet's children so the panel param resolves on direct deep links too.
      itemId: this.getParamFromRouteTree(CXP_ROUTE_PARAM_ITEM_ID),
    };
  }

  /**
   * Depth-first search of the whole snapshot tree (all outlets, not just the
   * primary `firstChild` chain) for the first route segment carrying `key`.
   * A named-outlet child (the CRL panel) lives in a parent's `children`, so the
   * earlier `firstChild`-only walk would miss it.
   */
  private getParamFromRouteTree(key: string): number | null {
    const stack: ActivatedRouteSnapshot[] = [this.router.routerState.snapshot.root];
    while (stack.length > 0) {
      const route = stack.pop()!;
      if (route.params[key] !== undefined) {
        const value = +route.params[key];
        return isNaN(value) ? null : value;
      }
      stack.push(...route.children);
    }
    return null;
  }
}
